import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BACKEND_URL } from "@/lib/api";

const BrandingContext = createContext(null);

const DEFAULTS = {
  company_name: "MEP Engineering Consultancy",
  platform_name: "MEP",
  platform_suffix: ".Engine",
  owner_name: "FURD Tech",
  tagline: "Mechanical, Electrical & Public Health Engineering",
  login_title: "Structured MEP pricing for engineering consultancies",
  login_subtitle: "",
  logo_url: "",
  favicon_url: "",
  primary_color: "#3B82F6",
  accent_color: "#3B82F6",
  show_owner_name: true,
};

const CACHE_KEY = "mep_branding_cache";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCachedBranding() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts < CACHE_TTL) return data;
    }
  } catch {}
  return null;
}

function setCachedBranding(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => getCachedBranding() || DEFAULTS);

  // Fetch branding once on mount — use cache to avoid unnecessary network calls
  useEffect(() => {
    let cancelled = false;
    const cached = getCachedBranding();
    if (cached) {
      setBranding(cached);
      return; // Use cache, skip network
    }
    fetch(`${BACKEND_URL}/api/branding/public`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const merged = { ...DEFAULTS, ...data };
        setBranding(merged);
        setCachedBranding(merged);
      })
      .catch(() => {}); // Use DEFAULTS fallback
    return () => { cancelled = true; };
  }, []);

  // Invalidate and re-fetch (called after admin changes branding)
  const refreshBranding = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    fetch(`${BACKEND_URL}/api/branding/public`)
      .then(r => r.json())
      .then(data => {
        const merged = { ...DEFAULTS, ...data };
        setBranding(merged);
        setCachedBranding(merged);
      })
      .catch(() => {});
  }, []);

  // Direct update without re-fetch (used by admin/onboarding after save)
  const updateBranding = useCallback((data) => {
    const merged = { ...DEFAULTS, ...data };
    setBranding(merged);
    setCachedBranding(merged);
  }, []);

  const ctx = { branding, refreshBranding, updateBranding };

  return <BrandingContext.Provider value={ctx}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
  return ctx;
}
